import React, { useState, useEffect } from 'react';
import { useApi } from '../api';
import {
  Typography, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip, Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SummarizeIcon from '@mui/icons-material/Summarize';
import BarChartIcon from '@mui/icons-material/BarChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { formatearPrecio, formatearFechaLocal } from './utils';

export default function IngresosGastos() {
  const hoy = new Date();
  const primerDia = formatearFechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const ultimoDia = formatearFechaLocal(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
  const api = useApi();

  const [modalOpen, setModalOpen] = useState(false);
  const [registros, setRegistros] = useState([]);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [registroActual, setRegistroActual] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(primerDia);
  const [fechaHasta, setFechaHasta] = useState(ultimoDia);
  const [busqueda, setBusqueda] = useState('');
  

  const [nuevoRegistro, setNuevoRegistro] = useState({
    descripcion: '',
    tipo: '',
    monto: '',
    fecha: formatearFechaLocal(hoy),
  });

  useEffect(() => {
    api.get(`${process.env.REACT_APP_API_URL}/api/ingresos-gastos/`)
      .then(response => setRegistros(response.data))
      .catch(error => console.error('Error al cargar registros:', error));
  }, [api]);

  const abrirModal = () => {
    setModoEdicion(false);
    setNuevoRegistro({
      descripcion: '',
      tipo: '',
      monto: '',
      fecha: formatearFechaLocal(hoy),
    });
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setRegistroActual(null);
  };

  const guardarRegistro = () => {
    if (!nuevoRegistro.descripcion || !nuevoRegistro.tipo || !nuevoRegistro.monto) {
      alert('Todos los campos son obligatorios');
      return;
    }

    if (modoEdicion && registroActual) {
      api.put(`${process.env.REACT_APP_API_URL}/api/ingresos-gastos/${registroActual.id}/`, nuevoRegistro
      )
        .then(response => {
          const actualizados = registros.map(r => r.id === registroActual.id ? response.data : r);
          setRegistros(actualizados);
          cerrarModal();
        })
        .catch(error => console.error('Error al actualizar:', error));
    } else {
      api.post(`${process.env.REACT_APP_API_URL}/api/ingresos-gastos/`, nuevoRegistro)
      .then(response => {
        setRegistros([...registros, response.data]);
        cerrarModal();
      })
      .catch(error => console.error('Error al guardar:', error));

    }
  };

  const eliminarRegistro = (id) => {
    api.delete(`${process.env.REACT_APP_API_URL}/api/ingresos-gastos/${id}/`)
      .then(() => {
        const filtrados = registros.filter((r) => r.id !== id);
        setRegistros(filtrados);
      })
      .catch(error => console.error('Error al eliminar:', error));
  };

  const editarRegistro = (registro) => {
    setModoEdicion(true);
    setRegistroActual(registro);
    setNuevoRegistro({
      descripcion: registro.descripcion,
      tipo: registro.tipo,
      monto: registro.monto ? String(Math.round(parseFloat(registro.monto))) : '',
      fecha: registro.fecha,
    });
    setModalOpen(true);
  };

  const formatearMiles = (valor) => {
    const soloDigitos = String(valor ?? '').replace(/\D/g, '');
    if (!soloDigitos) return '';
    return new Intl.NumberFormat('es-CO').format(Number(soloDigitos));
  };

  const handleMontoChange = (e) => {
    const soloDigitos = e.target.value.replace(/\D/g, '');
    setNuevoRegistro({ ...nuevoRegistro, monto: soloDigitos });
  };

  const registrosFiltrados = registros.filter((r) => {
    const fecha = new Date(r.fecha);
    const desde = fechaDesde ? new Date(fechaDesde) : null;
    const hasta = fechaHasta ? new Date(fechaHasta) : null;

    const pasaFiltroFecha = (!desde || fecha >= desde) && (!hasta || fecha <= hasta);
    const pasaBusqueda = r.descripcion.toLowerCase().includes(busqueda.toLowerCase());

    return pasaFiltroFecha && pasaBusqueda;
  });

  const calcularResumen = () => {
    const ingresos = registrosFiltrados
      .filter((r) => r.tipo === 'Ingreso')
      .reduce((sum, r) => sum + parseFloat(r.monto), 0);
    const gastos = registrosFiltrados
      .filter((r) => r.tipo === 'Gasto')
      .reduce((sum, r) => sum + parseFloat(r.monto), 0);
    const saldo = ingresos - gastos;
    return { ingresos, gastos, saldo };
  };

  const calcularTotales = (lista = []) => {
    const ingresos = lista
      .filter((r) => r.tipo === 'Ingreso')
      .reduce((sum, r) => sum + parseFloat(r.monto), 0);
    const gastos = lista
      .filter((r) => r.tipo === 'Gasto')
      .reduce((sum, r) => sum + parseFloat(r.monto), 0);
    return [
      { tipo: 'Ingreso', monto: ingresos },
      { tipo: 'Gasto', monto: gastos },
    ];
  };

  const exportarAExcel = () => {
    if (registrosFiltrados.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }

    const data = registrosFiltrados.map(r => ({
      Fecha: r.fecha,
      Descripción: r.descripcion,
      Tipo: r.tipo,
      Monto: parseFloat(r.monto)
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "IngresosGastos");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `IngresosGastos-${formatearFechaLocal()}.xlsx`);
  };

  const resumen = calcularResumen();
  const resumenGrafico = calcularTotales(registrosFiltrados);

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Título principal animado */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Typography className="titulo-app" variant="h5" gutterBottom>
          <AccountBalanceWalletIcon sx={{ mr: 1, fontSize: 32, color: '#083a94', verticalAlign: 'middle' }} />
          Ingresos y Gastos
        </Typography>
      </motion.div>

      {/* Sección: Resumen financiero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #10b981', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#065f46', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SummarizeIcon sx={{ fontSize: 24 }} />
            Resumen financiero
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
                <Typography variant="subtitle1">Total Ingresos</Typography>
                <Typography variant="h6" color="green">${formatearPrecio(resumen.ingresos)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#ffebee' }}>
                <Typography variant="subtitle1">Total Gastos</Typography>
                <Typography variant="h6" color="red">${formatearPrecio(resumen.gastos)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                <Typography variant="subtitle1">Saldo Neto</Typography>
                <Typography variant="h6" color={resumen.saldo >= 0 ? 'green' : 'red'}>
                  ${formatearPrecio(resumen.saldo)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Sección: Resumen gráfico */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #3b82f6', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#1d4ed8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChartIcon sx={{ fontSize: 24 }} />
            Resumen gráfico
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={resumenGrafico}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="tipo" />
              <Tooltip />
              <Legend />
              <Bar dataKey="monto" fill="#1976d2" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </motion.div>

      {/* Sección: Registros */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.35, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #083a94', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#083a94', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ListAltIcon sx={{ fontSize: 24 }} />
            Registros
          </Typography>

          {/* Filtros y Botones */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={abrirModal}
            >
              AGREGAR REGISTRO
            </Button>
            <TextField
              label="Desde"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
            <TextField
              label="Hasta"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
            <TextField
              label="Buscar"
              placeholder="Descripción"
              size="small"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <Button
              variant="outlined"
              color="success"
              onClick={exportarAExcel}
            >
              Exportar a Excel
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Monto</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrosFiltrados.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.fecha}</TableCell>
                    <TableCell>{row.descripcion}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.tipo}
                        color={row.tipo === 'Ingreso' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>${formatearPrecio(row.monto)}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => editarRegistro(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => eliminarRegistro(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </motion.div>

      {/* Modal */}
      <Dialog open={modalOpen} onClose={cerrarModal} PaperProps={{ sx: { width: '100%', maxWidth: 480 } }}>
        <DialogTitle>{modoEdicion ? 'Editar Registro' : 'Agregar Ingreso/Gasto'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Descripción"
            fullWidth
            value={nuevoRegistro.descripcion}
            onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, descripcion: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Tipo"
            select
            fullWidth
            value={nuevoRegistro.tipo}
            onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, tipo: e.target.value })}
          >
            <MenuItem value="Ingreso">Ingreso</MenuItem>
            <MenuItem value="Gasto">Gasto</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Monto"
            type="text"
            inputMode="numeric"
            fullWidth
            value={formatearMiles(nuevoRegistro.monto)}
            onChange={handleMontoChange}
          />
          <TextField
            margin="dense"
            label="Fecha"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={nuevoRegistro.fecha}
            onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, fecha: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarModal}>Cancelar</Button>
          <Button onClick={guardarRegistro} variant="contained">
            {modoEdicion ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
